import { create } from 'zustand';
import { cabinet } from '../api/cabinet';
import { api, isUnauthorized } from '../api/client';
import type { Me } from '../api/types';
import { useI18n } from '../i18n';

type Status = 'unknown' | 'loading' | 'authed' | 'anon' | 'error';

interface SessionState {
  status: Status;
  me: Me | null;
  /** Probe /auth/me. A 401 (or a staff session) means "not signed in as a client". */
  load: () => Promise<void>;
  logout: () => Promise<void>;
  /** Called when any request gets a 401. */
  expire: () => void;
}

export const useSession = create<SessionState>((set) => ({
  status: 'unknown',
  me: null,
  async load() {
    set((s) => ({ status: s.status === 'authed' ? 'authed' : 'loading' }));
    try {
      const me = await cabinet.me();
      if (me?.role !== 'client') {
        set({ status: 'anon', me: null });
        return;
      }
      set({ status: 'authed', me });
      const l = me.contact?.locale;
      if (l) useI18n.getState().setContactLocale(l);
    } catch (e) {
      set({ status: isUnauthorized(e) ? 'anon' : 'error', me: null });
    }
  },
  async logout() {
    try {
      await cabinet.logout();
    } catch {
      /* the cookie may already be gone: still leave */
    }
    set({ status: 'anon', me: null });
  },
  expire() {
    set({ status: 'anon', me: null });
  },
}));

api.setOnUnauthorized(() => useSession.getState().expire());
